import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { urlConstants } from '../common/urlConstants';
import { GridColumnHeader } from '../models/grid-column-header';
import { UserSystemPreference } from '../models/userSystemPreference';
import { SaveColumnsReorder } from '../models/save-Columns-Reorder';
import { ReAssignReleaseDto } from '../models/reAssignReleaseDto';
import { ActivateSuspendDto } from '../models/activateSuspendDto';
import { DocumentNotesData } from '../models/document-notes.model';
import { GlobalService } from './global.service';
import { DirectRetrievalDto } from '../models/directRetrievalDto';
import { UserSecurityProfileDto } from '../models/userSecurityProfileDto';
import { DialogComponent } from '../shared/dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { VendorClassificationDto, VendorClassificationResponseDto, VendorClassLookupRequest, VendorDeleteDto } from '../models/vendorDetailsDtos';
import { UserSystemInfoDto } from '../models/userSystemInfoDtos';
import { AbbyyLineItemsDto, GLAccountLookupRequest, GlEntryResponseDto, LineItemResponseDto } from '../models/lineItemsDtos';
import { PaymentCodeClassificationResponseDto,UpdatePaymentCode } from '../models/paymentCode';

@Injectable({
  providedIn: 'root'
})
export class CommonService { 
  private readonly messageSource = new BehaviorSubject<string>('');
  private readonly typeSource = new BehaviorSubject<'success' | 'error' | ''>('');

  currentMessage$ = this.messageSource.asObservable();
  currentType$ = this.typeSource.asObservable();
  constructor(
    private readonly http: HttpClient, 
    private readonly globalVariable: GlobalService, 
    private readonly dialog: MatDialog
  ) { }

  showMessage(message: string, type: 'success' | 'error', hide:boolean = true) {
    this.messageSource.next(message);
    this.typeSource.next(type);
    if (hide) {
      setTimeout(() => {
        this.clearMessage();
      }, 10000);
    }
  }

  clearMessage() {
    this.messageSource.next('');
    this.typeSource.next('');
  }

  getAllSystemInfo(): Observable<any> {
    const allSystemInfoUrl = `${environment.apiUrl}${urlConstants.userAllSystemInfoUrl}`;
    return this.http.get<any>(allSystemInfoUrl);
  }

  getUserSystemInfo(systemId: number = 6000): Observable<UserSystemInfoDto> {
    const userSystemInfoUrl = `${environment.apiUrl}${urlConstants.userSystemInfoUrl}${this.globalVariable.getUserGPID}/${systemId}`;
    return this.http.get<UserSystemInfoDto>(userSystemInfoUrl);
  }

  callRegisterPrincipal(systemId: number): Observable<any> {
    const registerPrincipleUrl = `${environment.apiUrl}${urlConstants.registerPrincipleUrl}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<any>(registerPrincipleUrl);
  }

  getGridColumnHeaders(gridId: number, businessAreaId: number, queueId: number, workflowId: number, systemId: number): Observable<GridColumnHeader[]> {
    const getGridColumnHeadersUrl = `${environment.apiUrl}${urlConstants.getGridColumnHeadersURL}${this.globalVariable.getUserGPID}/` +
      `${this.globalVariable.getSelectedSystemId}/${gridId}/${businessAreaId}/${queueId}/${workflowId}`;
    return this.http.get<GridColumnHeader[]>(getGridColumnHeadersUrl);
  }

  getUserPerferences(): Observable<UserSystemPreference[]> {
    const routeUrl = `${environment.apiUrl}${urlConstants.getUserPreferences}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<UserSystemPreference[]>(routeUrl);
  }

  getContact(): Observable<any> {
    const routeUrl = `${environment.apiUrl}${urlConstants.contactUsAndUserGuide}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/CONTACT`;
    return this.http.get<any>(routeUrl);
  }

  getUserGuide(): Observable<any> {
    const routeUrl = `${environment.apiUrl}${urlConstants.contactUsAndUserGuide}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/USER_GUIDELINES`;
    return this.http.get<any>(routeUrl);
  }

  saveUserPreferencesColumns(saveColumnReorderDto: SaveColumnsReorder, isDelete: number, userGPID: string, systemId: number): Observable<boolean> {
    let saveUserPrefURL;
    if (this.globalVariable.getSelectedSystemId) {
      saveColumnReorderDto.systemId = Number(this.globalVariable.getSelectedSystemId);
    }
    if (this.globalVariable.getUserGPID) {
      saveColumnReorderDto.userName = this.globalVariable.getUserGPID;
    }
    const deleteFlag = (isDelete === 0 || isDelete == null) ? 0 : isDelete;
    saveUserPrefURL = `${environment.apiUrl}${urlConstants.saveUserPreferencesColumns}${deleteFlag}`;
    return this.http.post<boolean>(saveUserPrefURL, saveColumnReorderDto);
  }

  logOut(): Observable<any> {
    const logOutURL = `${environment.apiUrl}${urlConstants.logOut}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<any>(logOutURL);
  }

  getReAssignUsers(userGPID: string, systemId: string): Observable<any> {
    const reAssignUsersURL = `${environment.apiUrl}${urlConstants.reAssignItemGpIds}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<any>(reAssignUsersURL);
  }

  getAlterUsers(userGPID: string, systemId: string): Observable<any> {
    const alterUsersURL = `${environment.apiUrl}${urlConstants.alterItemGpIds}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<any>(alterUsersURL);
  }

  assignReleaseWorkItemWorkFlow(postObj: ReAssignReleaseDto): Observable<any> {
    const reAssignWorkItemWorkFlow = `${environment.apiUrl}${urlConstants.reAssignWorkItemWorkFlow}`;
    return this.http.post(reAssignWorkItemWorkFlow, postObj, { responseType: 'text' });
  }

  activateSuspendWorkItem(postObj: ActivateSuspendDto): Observable<any> {
    const activateSuspendWorkItem = `${environment.apiUrl}${urlConstants.activateWorkItem}`;
    return this.http.post(activateSuspendWorkItem, postObj, { responseType: 'text' });
  }

  getLinkEmailInfo(userGPID: string, systemId: string, documentId: string): Observable<any> {
    const emailInfoURL = `${environment.apiUrl}${urlConstants.email}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${documentId}/document`;
    return this.http.get<any>(emailInfoURL);
  }

  sendEmail(formbody: any, documentId: string): Observable<boolean> {
    const routeUrl = `${environment.apiUrl}${urlConstants.sendEmail}/${documentId}/document`;
    return this.http.post<boolean>(routeUrl, formbody);
  }

  getNotesDetails(userGPID: string, systemId: string, instanceId: string): Observable<DocumentNotesData[]> {
    const documentNotesDetailsURL = `${environment.apiUrl}${urlConstants.viewDocumentNotes}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${instanceId}`;
    return this.http.get<DocumentNotesData[]>(documentNotesDetailsURL);
  }

  addDocumentNotes(userGPID: string, systemId: string, documentId: string, postObj: any): Observable<boolean> {
    const addNotesURL = `${environment.apiUrl}${urlConstants.addDocumentNotes}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${documentId}`;
    return this.http.post<boolean>(addNotesURL, postObj);
  }

  addWorkflowNotes(userGPID: string, systemId: string, instanceId: string, postObj: any): Observable<boolean> {
    const addNotesURL = `${environment.apiUrl}${urlConstants.addNotes}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${instanceId}`;
    return this.http.post<boolean>(addNotesURL, postObj);
  }

  getWorkFlowNotesDetailsByBatchId(userGPID: string, systemId: string, batchId: string): Observable<DocumentNotesData[]> {
    const documentNotesDetailsURL = `${environment.apiUrl}${urlConstants.getWorkItemNotesByBatchIDURL}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${batchId}`;
    return this.http.get<DocumentNotesData[]>(documentNotesDetailsURL);
  }

  getWorkFlowNotesDetailsByInstanceorDocId(userGPID: string, systemId: string, instanceId: string): Observable<DocumentNotesData[]> {
    const documentNotesDetailsURL = `${environment.apiUrl}${urlConstants.viewWorkFlowNotes}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${instanceId}`;
    return this.http.get<DocumentNotesData[]>(documentNotesDetailsURL);
  }

  getDocumentsLinkInfo(userGPID: string, systemId: string, documentId: string): Observable<any> {
    const documentsInfoURL = `${environment.apiUrl}${urlConstants.documentsInfoURL}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}/${documentId}`;
    return this.http.get<any>(documentsInfoURL);
  }

  sendAutoEmail(requestObj: any, items: any): Observable<boolean> {
    const isAttachment = 1;
    const sendEmailUrl = `${environment.apiUrl}${urlConstants.sendEmail}/${items.Id}/${items.Type}/${isAttachment}/true`;
    return this.http.post<any>(sendEmailUrl, requestObj);
  }

  saveUserPerferences(usersysPref: any): Observable<any> {
    const systemId = localStorage.getItem('SelectedSystemId');
    const saveUserPrefURL = `${environment.apiUrl}${urlConstants.saveUserPreferences}?systemId=${systemId}`;
    return this.http.post<boolean>(saveUserPrefURL, usersysPref);
  }

  getDirectRetrieval(systemId: string | null, documentID: string | null, batchId: string | null): Observable<DirectRetrievalDto> {
    let directDocumentRetrievalUrl: string;

    if (batchId) {
      directDocumentRetrievalUrl = `${environment.apiBaseUrl}${urlConstants.directRetrieval}${systemId}/${batchId}/batch`;
    } else {
      directDocumentRetrievalUrl = `${environment.apiBaseUrl}${urlConstants.directRetrieval}${systemId}/${documentID}/document`;
    }
    return this.http.get<DirectRetrievalDto>(directDocumentRetrievalUrl);
  }

  getUserPermissions(userGPID: string, systemId: string): Observable<UserSecurityProfileDto> {
    const getUserPermissionsURL = `${environment.apiUrl}${urlConstants.userPermissions}${userGPID}/${systemId}`;
    return this.http.get<UserSecurityProfileDto>(getUserPermissionsURL);
  }

  uploadAttachments(files: File[], userGpid: string, systemId: string, documentId: string) {
    const addPageURL = `${environment.apiUrl}${urlConstants.addAttachments}${userGpid}/${systemId}/${documentId}`;
    const formData: FormData = new FormData();
    files.forEach(file => formData.append('file', file));
    return this.http.post(addPageURL, formData, { responseType: 'text' });
  }

  getUploadFileOptions(): Observable<any> {
    const getUploadFileOptionsURL = `${environment.apiUrl}${urlConstants.getFile}${this.globalVariable.getUserGPID}/${this.globalVariable.getSelectedSystemId}`;
    return this.http.get<any>(getUploadFileOptionsURL);
  }

  getVendorClassifications(userGPID: string, systemId: string, searchVName: string, searchCompanyCode: string ,pageNo: number = 0, pageSize: number = 0): Observable<VendorClassificationResponseDto> {
    const getVendorClassificationsURL = `${environment.apiUrl}${urlConstants.getVendorClassifications}${userGPID}/${systemId}/${pageNo}/${searchVName}/${searchCompanyCode}/${pageSize}`;
    return this.http.get<VendorClassificationResponseDto>(getVendorClassificationsURL);
  }

  getGetAbbyyLineItems(userGPID: string, systemId: string, documentId: string, type: string, pageNo: number = 0): Observable<LineItemResponseDto | GlEntryResponseDto> {
    const getAbbyyLineItemsURL = `${environment.apiUrl}${urlConstants.getAbbyyLineItems}${userGPID}/${systemId}/${pageNo}/${documentId}/${type}`;
  return this.http.get<LineItemResponseDto | GlEntryResponseDto>(getAbbyyLineItemsURL);
  }

  openErrorDialog(message: string): void {
    const dialogRef = this.dialog.open(DialogComponent, {
      data: {
        title: 'ERROR',
        message: message,
        showOkButton: true,
        showCancelButton: false,
        showSubmitButton: false,
      },
    });

    dialogRef.afterClosed().subscribe();
  }

  saveVendorClassifications(saveVendorClassification: VendorClassificationDto): Observable<boolean> {
    const saveVendorClassificationURL = `${environment.apiUrl}${urlConstants.saveVendorClassification}`;
    return this.http.post<any>(saveVendorClassificationURL, saveVendorClassification);
  }

  deleteVendorClassifications(payload:VendorDeleteDto): Observable<boolean> {
    const deleteVendorClassificationURL = `${environment.apiUrl}${urlConstants.deleteVendorClassificationData}`;
    return this.http.post<any>(deleteVendorClassificationURL, payload);;
  }

  saveAbbyyLineItems(saveAbbyyLineItemDetails: AbbyyLineItemsDto): Observable<boolean> {
    const saveAbbyyDetailsURL = `${environment.apiUrl}${urlConstants.saveAbbyyLineItems}`;
    return this.http.post<any>(saveAbbyyDetailsURL, saveAbbyyLineItemDetails);
  }

  deleteGLLineItem(userGPID: string, systemId: string, documentId: string, docItemNumber: string): Observable<boolean> {
    const deleteGLLineItemURL = `${environment.apiUrl}${urlConstants.deleteGLLineItem}${userGPID}/${systemId}/${documentId}/${docItemNumber}`;
    return this.http.get<boolean>(deleteGLLineItemURL);
  }
  getGLAutoComboValueLookup<T>(glAccountLookupRequest: GLAccountLookupRequest): Observable<T[]> {
    const getGLAutoComboValueLookupURL = `${environment.apiUrl}${urlConstants.getGLAutoComboValueLookup}`;
    return this.http.post<T[]>(getGLAutoComboValueLookupURL, glAccountLookupRequest);
  }
  getVendorClassAutoComboValueLookup<T>(vendorClassLookupRequest: VendorClassLookupRequest): Observable<T[]> {
    const getVendorClassLookupRequestURL = `${environment.apiUrl}${urlConstants.getVendorClassificationAutoComboValueLookup}`;
    return this.http.post<T[]>(getVendorClassLookupRequestURL, vendorClassLookupRequest);
  }
  savePaymentCode(savePaymentCode: UpdatePaymentCode): Observable<boolean> {
    const savePaymentCodeURL = `${environment.apiUrl}${urlConstants.savePaymentCode}`;
    return this.http.post<any>(savePaymentCodeURL, savePaymentCode);
  }
  deletePaymentCode(savePaymentCode: UpdatePaymentCode): Observable<boolean> {
    const deletePaymentCode = `${environment.apiUrl}${urlConstants.savePaymentCode}`;
    return this.http.post<any>(deletePaymentCode, savePaymentCode);
  }
  getPaymentCodes(userGPID: string, systemId: string,pageNo: number = 0): Observable<PaymentCodeClassificationResponseDto> {
    const getPaymentCodeURL = `${environment.apiUrl}${urlConstants.getPaymentCode}${userGPID}/${systemId}/${pageNo}`;
    return this.http.get<PaymentCodeClassificationResponseDto>(getPaymentCodeURL);
  }
}
